import { useCallback, useEffect, useMemo, useState } from 'react';
import { useProjectStream, type ProjectEvent } from '../api/projectStream.js';
import { ingestProjectEvent } from '../stores/quoteJobsStore.js';
import {
  Button,
  MessageBar,
  MessageBarBody,
  Select,
  Spinner,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Toast,
  ToastBody,
  ToastTitle,
  Tooltip,
  makeStyles,
  mergeClasses,
  useToastController,
} from '@fluentui/react-components';
import {
  ArrowLeft24Regular,
  CheckmarkCircle24Regular,
  Delete24Regular,
  Edit24Regular,
  Sparkle24Regular,
  ShieldCheckmark20Regular,
  Warning24Regular,
} from '@fluentui/react-icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ApiClientError } from '../api/client.js';
import {
  deleteFile as apiDeleteFile,
  deleteProject as apiDeleteProject,
  reparseFile as apiReparseFile,
  getProject,
  updateFileClassification,
  uploadFiles,
  type Project,
  type ProjectFile,
} from '../api/projects.js';
import { createQuote, setQuoteSource, setQuoteCatalogs } from '../api/quotes.js';
import { debeSincronizarSeleccion } from '../lib/seleccionDelPresupuesto.js';
import { getProjectCatalogs } from '../api/projectCatalogs.js';
import { GLOBAL_TOASTER_ID } from '../components/ThemedProvider.js';
import { AppTopBar } from '../components/AppTopBar.js';
import { useConfirm } from '../components/ConfirmDialog.js';
import { EditProjectDialog } from '../components/EditProjectDialog.js';
import { getObraContext } from '../api/apuIa.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { DropZone } from '../components/DropZone.js';
import { FilesPanel } from '../components/FilesPanel.js';
import { IndexProgress } from '../components/IndexProgress.js';
import { ChatPanel } from '../components/ChatPanel.js';
import { DemoSampleChat } from '../components/DemoSampleChat.js';
import { ObraSummary } from '../components/ObraSummary.js';
import { JuntaPanel } from '../components/JuntaPanel.js';
import { JUNTA_ENABLED } from '../lib/flags.js';
import { productoActivo } from '../lib/productoActivo.js';
import type { AnalyzeProgressEvent, QuoteProgressEvent } from '../api/projectStream.js';
import { useCanMutate, useIsEcosoftStaff } from '../stores/authStore.js';
import { useNovedad, useNovedadStyles } from '../components/Novedad.js';
import { formatoDeMoneda } from '../lib/money.js';
import { proponerCatalogo } from '../lib/catalogoPropuesto.js';
import { disciplinaLabel, estatusBadge } from '../lib/projectDisplay.js';
import { PlanosGaleria } from '../components/planos/PlanosGaleria.js';
import { MiniaturaIfcEnSegundoPlano } from '../components/planos/MiniaturaIfcEnSegundoPlano.js';
import { useMiniaturasIfcEnSegundoPlano } from '../components/planos/useMiniaturasIfcEnSegundoPlano.js';
import { setPlanoEscala } from '../api/planos.js';
import { planoPrincipal, usePlanosGaleria } from '../components/planos/usePlanosGaleria.js';
import { tipoDePlanoPorNombre } from '@supernova/shared';

const fmtNum = new Intl.NumberFormat('es-MX');

/**
 * True mientras el proyecto todavía está leyendo o analizando algún documento.
 *
 * Cubre TODO el pipeline, incluidos los huecos entre fases: tras leer un archivo
 * (`parse_status='ok'`) el índice se queda unos segundos en `'idle'` antes de
 * pasar a `'chunking'` → `'embedding'` → `'ready'`. Si solo miráramos
 * chunking/embedding, en esos huecos el botón "Armar presupuesto" se habilitaba
 * y dejaba armarlo con el documento a medio analizar (bug EJEMPLO IV).
 *
 * Regla de raíz: hay documentos ya leídos OK cuyo índice todavía NO llegó a un
 * estado terminal (`ready`/`partial`/`failed`), o algún archivo sigue en cola o
 * leyéndose. Un proyecto sin archivos, o cuyos archivos fallaron al leerse (sin
 * ninguno OK), NO cuenta como "procesando" — ahí aplica otro estado (subir/
 * reintentar), no el aviso de "espera".
 */
function projectIsProcessing(project: Project, files: ProjectFile[]): boolean {
  const parsing = files.some(
    (f) => f.parseStatus === 'pending' || f.parseStatus === 'parsing',
  );
  const anyParsedOk = files.some((f) => f.parseStatus === 'ok');
  const indexTerminal =
    project.indexStatus === 'ready' ||
    project.indexStatus === 'partial' ||
    project.indexStatus === 'failed';
  return parsing || (anyParsedOk && !indexTerminal);
}

const useStyles = makeStyles({
  page: {},
  content: {
    padding: '26px var(--space-xxl)',
    maxWidth: '1320px',
    width: '100%',
    margin: '0 auto',
  },
  head: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 'var(--space-l)',
    marginBottom: 'var(--space-l)',
    flexWrap: 'wrap',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--fs-600)',
    fontWeight: 700,
    margin: 0,
    letterSpacing: '-0.01em',
    color: 'var(--text-primary)',
  },
  sub: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    color: 'var(--text-secondary)',
    fontSize: 'var(--fs-300)',
    margin: '6px 0 0',
  },
  desc: { color: 'var(--text-tertiary)', fontSize: 'var(--fs-200)', margin: '4px 0 0' },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '22px',
    padding: '0 9px',
    borderRadius: 'var(--radius-circular)',
    fontSize: '11.5px',
    fontWeight: 650,
  },
  dot: { width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' },
  // Alineado a la derecha aunque el título largo empuje las acciones a otra línea
  // (marginLeft auto). flexWrap + flex-end: si los propios botones no caben,
  // envuelven pegados a la derecha en vez de desbordar.
  headActions: { display: 'flex', gap: 'var(--space-s)', flexShrink: 0, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', marginLeft: 'auto', maxWidth: '100%' },
  // Semáforo del contexto de proyecto (mismo criterio que la vista de presupuesto):
  // verde = ya ajustado; amarillo = pendiente / deducido.
  ctxDone: { border: '1.5px solid var(--ok)', background: 'var(--ok-weak)', color: 'var(--ok)' },
  ctxPending: { border: '1.5px solid var(--warn)', color: 'var(--warn)' },
  ctxGroup: { display: 'inline-flex', alignItems: 'center', gap: '5px', maxWidth: '100%' },
  helpBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '19px',
    height: '19px',
    borderRadius: '50%',
    border: '1.5px solid var(--border-default)',
    color: 'var(--text-tertiary)',
    fontSize: '11px',
    fontWeight: 700,
    cursor: 'pointer',
    background: 'var(--bg-surface)',
    flex: 'none',
    ':hover': { border: '1.5px solid var(--accent)', color: 'var(--accent)' },
  },
  helpCard: { maxWidth: '340px', fontSize: 'var(--fs-200)', lineHeight: 1.45, color: 'var(--text-primary)' },
  helpCardTitle: { fontWeight: 700, color: 'var(--accent)', marginBottom: '6px' },
  helpRow: { display: 'flex', gap: '7px', marginTop: '6px', alignItems: 'flex-start' },
  summary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1px',
    background: 'var(--border-subtle)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    marginBottom: 'var(--space-l)',
  },
  cell: { background: 'var(--bg-surface)', padding: '15px 17px' },
  lab: {
    fontSize: '10.5px',
    letterSpacing: '0.7px',
    textTransform: 'uppercase',
    color: 'var(--text-tertiary)',
    fontWeight: 650,
  },
  big: {
    fontSize: 'var(--fs-600)',
    fontWeight: 700,
    margin: '5px 0 2px',
    letterSpacing: '-0.5px',
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-primary)',
  },
  note: { fontSize: '11.5px', color: 'var(--text-secondary)' },
  tabs: {
    display: 'flex',
    gap: '3px',
    borderBottom: '1px solid var(--border-subtle)',
    marginBottom: 'var(--space-l)',
  },
  tab: {
    padding: '10px 15px',
    fontSize: '13.5px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    marginBottom: '-1px',
    cursor: 'pointer',
    transitionProperty: 'color, border-color',
    transitionDuration: '150ms',
    ':hover': { color: 'var(--text-primary)' },
  },
  tabActive: { color: 'var(--accent)', borderBottom: '2px solid var(--accent)' },
  panel: {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-l)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-m)',
  },
  chatPanel: {
    height: 'calc(100vh - 340px)',
    minHeight: '420px',
    display: 'flex',
    flexDirection: 'column',
  },
  docsList: { display: 'flex', flexDirection: 'column', gap: 'var(--space-l)' },
  sectionLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: 'var(--space-s)',
  },
  trustbox: {
    border: '1px solid var(--oficial)',
    background: 'var(--oficial-weak)',
    borderRadius: 'var(--radius-md)',
    padding: '11px 13px',
    fontSize: '12px',
    color: 'var(--text-secondary)',
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
  },
  loading: { padding: 'var(--space-4xl)', display: 'grid', placeItems: 'center' },
});

export function ProjectWorkspacePage() {
  const styles = useStyles();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);
  const { id } = useParams<{ id: string }>();
  // ci = producto de planos: la pestaña de documentos es la GALERÍA de planos.
  const esCiProducto = productoActivo() === 'ci';
  const galeria = usePlanosGaleria(esCiProducto ? id : null);
  const recargarGaleria = galeria.recargar;
  const [project, setProject] = useState<Project | null>(null);
  // Costo en dólares de leer las láminas de planos. El API solo lo manda si el
  // usuario es staff Ecosoft; al cliente le llega null y no se muestra nada.
  const [planosCostoUsd, setPlanosCostoUsd] = useState<number | null>(null);
  // Láminas que todavía no se leyeron (0 = generar no consume por planos).
  const [planosPendientes, setPlanosPendientes] = useState<number | undefined>(undefined);
  const [files, setFiles] = useState<ProjectFile[]>([]);
  // El proyecto de ejemplo (demo) es de solo lectura para todos: nadie sube,
  // borra ni elimina. El chat/cotización quedan como CTA (no gastan tokens).
  const canMutate = useCanMutate() && !project?.isExample;
  const esStaffEcosoft = useIsEcosoftStaff();
  // Vista previa de los IFC recién subidos: se dibujan fuera de pantalla (el servidor no dibuja IFC) sin que nadie los abra.
  const tamanosDeArchivos = useMemo(() => new Map(files.map((f) => [f.id, f.sizeBytes] as const)), [files]);
  const miniaturaIfc = useMiniaturasIfcEnSegundoPlano({
    activo: esCiProducto && canMutate && !!id,
    planos: galeria.planos,
    tamanosBytes: tamanosDeArchivos,
    recargar: recargarGaleria,
  });
  // RFI / Junta de aclaraciones: abierto a cualquier usuario del programa (antes staff-only).
  // El build flag JUNTA_ENABLED sigue siendo el master switch.
  const juntaVisible = JUNTA_ENABLED;
  // "Novedad" en RFI hasta el primer uso (recién abierto al público).
  const novJunta = useNovedad('junta');
  const novStyles = useNovedadStyles();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [analyzeProgress, setAnalyzeProgress] = useState<AnalyzeProgressEvent['data'] | null>(null);
  const [quoteProgress, setQuoteProgress] = useState<QuoteProgressEvent['data'] | null>(null);
  void quoteProgress; // se captura del WS pero ya no se muestra acá (la cotización vive en su propia vista)
  // Documentos primero: el flujo natural es subir el listado antes de resumir.
  const [tab, setTab] = useState<'resumen' | 'docs' | 'chat' | 'junta'>('docs');
  const [cotizando, setCotizando] = useState(false);
  // Estado del contexto de proyecto para el semáforo del botón: verde si el
  // usuario ya lo ajustó (source != 'deduced'), amarillo si aún es deducido o
  // no existe. Mismo criterio que el botón en la vista de presupuesto.
  const [ctxAjustado, setCtxAjustado] = useState<boolean | null>(null);
  const refreshCtxAjustado = useCallback(() => {
    if (!id) return;
    getObraContext(id)
      .then((r) => setCtxAjustado(r.source !== 'deduced'))
      .catch(() => setCtxAjustado(false));
  }, [id]);
  useEffect(() => { refreshCtxAjustado(); }, [refreshCtxAjustado]);

  // Navegación desde el Contexto con "Generar resumen": abre la pestaña Resumen y
  // dispara la regeneración una vez. Consumimos el state para no re-disparar.
  const location = useLocation();
  const [autoGenSummary, setAutoGenSummary] = useState(false);
  useEffect(() => {
    const st = (location.state ?? null) as { openResumen?: boolean; autoGenerateSummary?: boolean } | null;
    if (st?.openResumen) setTab('resumen');
    if (st?.autoGenerateSummary) setAutoGenSummary(true);
    if (st?.openResumen || st?.autoGenerateSummary) {
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getProject(id);
      setProject(data.project);
      setPlanosCostoUsd(data.planosCostoUsd ?? null);
      setPlanosPendientes(data.planosPendientes);
      setFiles(data.files);
      setError(null);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.status === 404 ? 'Proyecto no encontrado' : err.message);
      } else {
        setError('Error cargando proyecto');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  // "Cotizar concurso": una cotización por proyecto. Si ya existe, abre esa.
  // Si no, crea la cotización con el archivo del listado de conceptos designado
  // (classification='catalogo', o el único archivo) y va directo a la vista de
  // cotización. Sin wizard intermedio.
  const onCotizar = useCallback(async () => {
    if (!project) return;
    if (project.latestQuoteId) {
      // Selección compartida (proyecto ↔ cotización): mientras el presupuesto
      // NO se haya armado, el proyecto manda, y al entrar se sincroniza su
      // archivo fuente y sus catálogos con la selección actual.
      //
      // PERO en un presupuesto YA ARMADO eso no se toca (2026-08-19). Ahí la
      // selección no es una preferencia: es el registro de con qué se costeó.
      // Reescribirla al entrar tenía dos consecuencias, las dos malas:
      //   1. Mirar un presupuesto terminado lo modificaba. Sebastián lo notó
      //      porque su `updated_at` se movía sin que él hiciera nada, y con eso
      //      no se puede distinguir "alguien editó" de "alguien miró" — justo
      //      lo que hace falta para auditar una incidencia.
      //   2. Si el catálogo oficial cambió desde que se cotizó (pasó el
      //      2026-08-18: se reemplazó su contenido), entrar dejaba el
      //      presupuesto diciendo que salió de un catálogo que ya no es el que
      //      le puso los precios. El rastro mentía, y es el mismo rastro que
      //      usamos para explicar por qué a una obra le faltaron análisis.
      if (debeSincronizarSeleccion(project.latestQuoteStatus)) {
        try {
          const catalogoFile = proponerCatalogo(files)?.archivo;
          const projCats = await getProjectCatalogs(project.id).catch(() => []);
          await Promise.all([
            catalogoFile ? setQuoteSource(project.latestQuoteId, catalogoFile.id).catch(() => {}) : Promise.resolve(),
            setQuoteCatalogs(project.latestQuoteId, projCats.map((pc) => pc.catalogId)).catch(() => {}),
          ]);
        } catch { /* noop */ }
      }
      navigate(`/project/${project.id}/quotes/${project.latestQuoteId}`);
      return;
    }
    // Blindaje: aunque el botón parpadee habilitado por un instante (latencia
    // del WebSocket / refresh justo entre fases del análisis), NO se crea el
    // presupuesto mientras un documento se está leyendo o indexando. Misma
    // fuente de verdad que el botón (bug EJEMPLO IV: se armaba a medio analizar).
    if (projectIsProcessing(project, files)) {
      dispatchToast(
        <Toast><ToastTitle>Espera a que terminen tus documentos</ToastTitle>
          <ToastBody>Todavía estamos leyendo lo que subiste. En cuanto termine podrás armar el presupuesto.</ToastBody></Toast>,
        { intent: 'warning', timeout: 5000 },
      );
      return;
    }
    // La MISMA propuesta que muestra la pantalla. No se puede usar un archivo que no
    // se haya nombrado: antes, cuando ninguno detectaba conceptos, el desplegable se
    // quedaba vacío pidiendo elegir y esto armaba el presupuesto con el más viejo.
    const source = proponerCatalogo(files)?.archivo;
    if (!source) {
      dispatchToast(
        <Toast><ToastTitle>Sube primero el archivo del listado de conceptos</ToastTitle>
          <ToastBody>Este proyecto no tiene documentos. Súbelos en la pestaña Documentos.</ToastBody></Toast>,
        { intent: 'warning', timeout: 5000 },
      );
      setTab('docs');
      return;
    }
    setCotizando(true);
    try {
      // Borrador (autoStart:false): NO corre todavía. Abre la vista de
      // cotización para que el usuario elija catálogos y dé "Generar cotización".
      // name: el API exige ≥2 chars; el backend puede renombrar con el proyecto.
      // Placeholder fijo (igual que QuoteWizard) evita fallar si el proyecto
      // tiene nombre de 1 carácter (válido en projects, no en quotes).
      const r = await createQuote(project.id, {
        name: 'Presupuesto',
        sourceFileId: source.id,
        options: { autoStart: false },
      });
      navigate(`/project/${project.id}/quotes/${r.quote.id}`);
    } catch (e) {
      dispatchToast(
        <Toast><ToastTitle>No se pudo crear el presupuesto</ToastTitle>
          <ToastBody>{e instanceof Error ? e.message : 'Error'}</ToastBody></Toast>,
        { intent: 'error', timeout: 6000 },
      );
    } finally {
      setCotizando(false);
    }
  }, [project, files, navigate, dispatchToast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // ====== Live stream de eventos del proyecto ======
  const handleEvent = useCallback((ev: ProjectEvent) => {
    // Alimenta el indicador global de cotización del topbar.
    if (id) ingestProjectEvent(id, ev);
    if (ev.type === 'parse:progress') {
      setFiles((prev) =>
        prev.map((f) =>
          f.id === ev.data.fileId
            ? {
                ...f,
                parseStatus: ev.data.status,
                classification: (ev.data.classification ?? f.classification) as typeof f.classification,
                parsedPages: ev.data.parsedPages ?? f.parsedPages,
                ocrUsed: ev.data.ocrUsed ?? f.ocrUsed,
                parseError: ev.data.parseError ?? f.parseError,
              }
            : f,
        ),
      );
      return;
    }
    if (ev.type === 'index:progress') {
      setProject((prev) =>
        prev
          ? {
              ...prev,
              indexStatus: ev.data.projectStatus ?? prev.indexStatus,
              chunksTotal: ev.data.chunksTotal ?? prev.chunksTotal,
              chunksEmbedded: ev.data.chunksEmbedded ?? prev.chunksEmbedded,
            }
          : prev,
      );
      // Si llega chunking:done para un archivo, refrescamos chunksCount.
      if (ev.data.stage === 'chunking' && ev.data.done && ev.data.fileId) {
        const fid = ev.data.fileId;
        const chunks = ev.data.chunks ?? 0;
        setFiles((prev) => prev.map((f) => (f.id === fid ? { ...f, chunksCount: chunks } : f)));
      }
      return;
    }
    if (ev.type === 'quote:progress') {
      setQuoteProgress(ev.data);
      return;
    }
    if (ev.type === 'analyze:progress') {
      setAnalyzeProgress(ev.data);
      // Cuando termina, sync el status del proyecto (ObraSummary se refresca solo).
      if (ev.data.stage === 'done' || ev.data.stage === 'failed') {
        setProject((prev) =>
          prev
            ? {
                ...prev,
                summaryStatus: ev.data.summaryStatus ?? prev.summaryStatus,
                summaryError: ev.data.error ?? prev.summaryError,
                summaryAt: ev.data.stage === 'done' ? new Date().toISOString() : prev.summaryAt,
              }
            : prev,
        );
      } else {
        setProject((prev) =>
          prev
            ? { ...prev, summaryStatus: ev.data.summaryStatus ?? prev.summaryStatus }
            : prev,
        );
      }
      return;
    }
  }, [id]);
  useProjectStream({ projectId: id, onEvent: handleEvent });

  // Respaldo del estado en vivo: el WebSocket es la vía normal para enterarse
  // de que los documentos terminaron de leerse/indexarse, pero si se pierde un
  // evento (reconexión, pestaña en segundo plano), el estado del cliente queda
  // congelado y el gate de "esperar a que se analicen los documentos" se
  // equivoca (bug EJEMPLO IV: dejaba armar el presupuesto a medio analizar).
  // Mientras el proyecto parezca estar procesando, se refresca contra el
  // servidor cada pocos segundos hasta que el índice llega a un estado terminal.
  useEffect(() => {
    if (!project || !projectIsProcessing(project, files)) return;
    const t = setInterval(() => { void refresh(); }, 4000);
    return () => clearInterval(t);
  }, [project, files, refresh]);

  const onUpload = useCallback(
    async (selected: File[]) => {
      if (!id) return;
      setUploading(true);
      setProgress(0);
      try {
        await uploadFiles(id, selected, setProgress);
        await refresh();
        if (esCiProducto) void recargarGaleria();
      } catch (err) {
        const msg = err instanceof ApiClientError ? err.message : 'Error de upload';
        setError(msg);
      } finally {
        setUploading(false);
        setProgress(0);
      }
    },
    [id, refresh, esCiProducto, recargarGaleria],
  );

  async function onDeleteFile(f: ProjectFile) {
    if (!id) return;
    if (!(await confirm({
      title: 'Eliminar documento',
      message: `¿Eliminar "${f.originalName}"?`,
      confirmLabel: 'Eliminar',
      destructive: true,
    }))) return;
    try {
      await apiDeleteFile(id, f.id);
      await refresh();
      if (esCiProducto) void recargarGaleria();
    } catch (err) {
      alert((err as Error).message);
    }
  }

  async function onRetryFile(f: ProjectFile) {
    if (!id) return;
    try {
      await apiReparseFile(id, f.id);
      await refresh();
    } catch (err) {
      alert((err as Error).message);
    }
  }

  async function onDeleteProject() {
    if (!project) return;
    if (!(await confirm({
      title: 'Eliminar proyecto',
      message: `¿Eliminar el proyecto "${project.name}" y todos sus archivos?`,
      confirmLabel: 'Eliminar',
      destructive: true,
    }))) return;
    try {
      await apiDeleteProject(project.id);
      navigate('/projects', { replace: true });
    } catch (err) {
      alert((err as Error).message);
    }
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <AppTopBar />
        <Breadcrumbs items={[{ label: 'Inicio', to: '/projects' }, { label: 'Proyectos' }]} />
        <div className={styles.loading}>
          <Spinner label="Cargando proyecto…" />
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className={styles.page}>
        <AppTopBar />
        <Breadcrumbs items={[{ label: 'Inicio', to: '/projects' }, { label: 'Proyectos' }]} />
        <div style={{ padding: 'var(--space-xxl)' }}>
          <MessageBar intent="error">
            <MessageBarBody>{error ?? 'Proyecto no disponible'}</MessageBarBody>
          </MessageBar>
          <Button
            appearance="subtle"
            icon={<ArrowLeft24Regular />}
            style={{ marginTop: 'var(--space-l)' }}
            onClick={() => navigate('/projects')}
          >
            Volver a proyectos
          </Button>
        </div>
      </div>
    );
  }

  // ci = producto de planos: sin "Resumen del concurso" ni "Junta de aclaraciones"
  // (son de licitación); el botón habla de planos. El "Contexto de la obra" (botón
  // aparte) SÍ se queda: alimenta el motor de precios igual que en ai.
  const esCi = esCiProducto;
  const planosDelProyecto = galeria.planos ?? [];
  // En ci no hay "análisis" de documentos que anunciar: la insignia dice cuántos planos hay.
  const badgeBase = estatusBadge(project);
  const badge =
    esCi && (badgeBase.label === 'Analizado' || badgeBase.label === 'Nuevo')
      ? {
          bg: 'var(--bg-overlay)',
          fg: 'var(--text-secondary)',
          label:
            planosDelProyecto.length === 0
              ? 'Sin planos'
              : `${planosDelProyecto.length} ${planosDelProyecto.length === 1 ? 'plano' : 'planos'}`,
        }
      : badgeBase;
  const disc = disciplinaLabel(project);
  // Documentos de apoyo (ci): todo lo que NO es plano. Los planos viven en la galería.
  const documentosDeApoyo = esCi ? files.filter((f) => tipoDePlanoPorNombre(f.originalName) == null) : files;
  const labelArmar = esCi ? 'Armar presupuesto de los planos' : 'Armar presupuesto';
  const isDemo = project.isExample === true;
  const total = project.quoteConcepts ?? 0;
  const priced = project.quoteConceptsPriced ?? 0;
  const pct = project.quotedPct ?? 0;
  const amount = project.quotedAmount ?? 0;
  // El label debe reflejar lo que hace onCotizar: abre la existente SOLO si hay
  // latestQuoteId. Basarlo en `total>0` daba "Abrir presupuesto" cuando los
  // contadores estaban poblados pero latestQuoteId aún no (staleness en iPad) y
  // el clic terminaba creando una cotización nueva.
  const hasQuote = !!project.latestQuoteId;
  const parsingInFlight = files.some(
    (f) => f.parseStatus === 'pending' || f.parseStatus === 'parsing',
  );
  // Proyecto "procesando": aún lee o indexa algún documento. Mientras tanto,
  // armar presupuesto y generar resumen darían resultados incompletos → se
  // bloquean hasta que los documentos estén listos. El contexto (formulario)
  // no depende de los documentos, así que sigue disponible. La regla vive en
  // projectIsProcessing (fuente única, compartida con onCotizar) para que el
  // botón y la acción no puedan divergir.
  const isProcessing = projectIsProcessing(project, files);
  // Proyecto SOLO con planos u otros archivos sin texto: terminó de leerse (no
  // procesa) pero no quedó texto que indexar (0 chunks). No hay de qué armar un
  // resumen ni consultas/chat: se avisa en cada pestaña en vez de dejar botones
  // muertos o (antes) un "leyendo" eterno. Ver parseFile.ts §8.b (cierra el índice).
  const sinTexto =
    !isProcessing &&
    (project.chunksTotal ?? 0) === 0 &&
    files.some((f) => f.parseStatus === 'ok');
  // Una sola respuesta, compartida con el botón de armar el presupuesto: lo que se
  // muestra acá es exactamente el archivo que se va a usar.
  const propuesta = proponerCatalogo(files);
  const proposedCatalog = propuesta?.archivo;

  return (
    <div className={styles.page}>
      <AppTopBar />
      <Breadcrumbs
        items={[
          { label: 'Inicio', to: '/projects' },
          { label: 'Proyectos', to: '/projects' },
          { label: project.name },
        ]}
      />
      <main className={styles.content}>
        {isDemo && (
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px',
              padding: '12px 16px', marginBottom: 'var(--space-l)',
              background: 'rgba(12,111,214,0.10)', border: '1px solid rgba(12,111,214,0.30)', borderRadius: '8px',
            }}
          >
            <div style={{ fontSize: 'var(--fs-200)' }}>
              <strong>Proyecto de ejemplo.</strong> Explóralo libremente —documentos, presupuesto y
              análisis— sin gastar tus créditos. Así se ve una obra resuelta con OPUS ai.
            </div>
            <Button appearance="primary" onClick={() => navigate('/projects')} style={{ flexShrink: 0 }}>
              Crear mi primera obra
            </Button>
          </div>
        )}
        {/* ===== Cabecera ===== */}
        <div className={styles.head}>
          <div>
            <h1 className={styles.title}>{project.name}</h1>
            <div className={styles.sub}>
              {disc && (
                <>
                  <span>{disc}</span>
                  <span>·</span>
                </>
              )}
              <span className={styles.badge} style={{ background: badge.bg, color: badge.fg }}>
                <span className={styles.dot} /> {badge.label}
              </span>
            </div>
            {project.description && <p className={styles.desc}>{project.description}</p>}
          </div>
          <div className={styles.headActions}>
            {/* Acciones directas — el menú "Acciones" escondía todo tras un
                clic extra (reunión 2026-07-03). */}
            {canMutate && (
              <EditProjectDialog
                project={project}
                onUpdated={(p) => setProject((prev) => (prev ? { ...prev, ...p } : p))}
                trigger={
                  <Button appearance="secondary" icon={<Edit24Regular />}>
                    Editar proyecto
                  </Button>
                }
              />
            )}
            <span className={styles.ctxGroup}>
              <Button
                appearance="secondary"
                className={mergeClasses(
                  ctxAjustado === true && styles.ctxDone,
                  ctxAjustado === false && styles.ctxPending,
                )}
                icon={
                  ctxAjustado === false ? (
                    <Warning24Regular />
                  ) : ctxAjustado === true ? (
                    <CheckmarkCircle24Regular />
                  ) : (
                    <Sparkle24Regular />
                  )
                }
                onClick={() => navigate(`/project/${project.id}/obra-context`)}
                data-demo="btn-contexto"
              >
                Contexto de proyecto
              </Button>
              {/* "?" con la explicación del semáforo (Popover: funciona en touch,
                  a diferencia de un tooltip por hover). */}
              <Popover withArrow positioning="below">
                <PopoverTrigger disableButtonEnhancement>
                  <span className={styles.helpBtn} role="button" tabIndex={0} aria-label="Ayuda del contexto de proyecto">?</span>
                </PopoverTrigger>
                <PopoverSurface>
                  <div className={styles.helpCard}>
                    <div className={styles.helpCardTitle}>Contexto de proyecto</div>
                    Describe la obra (ubicación, tipo de obra y condiciones del sitio) y se usa
                    para calcular los precios unitarios con inteligencia artificial.
                    <div className={styles.helpRow}>
                      <CheckmarkCircle24Regular style={{ color: 'var(--ok)', flex: 'none' }} />
                      <span><strong>Verde:</strong> ya está ajustado.</span>
                    </div>
                    <div className={styles.helpRow}>
                      <Warning24Regular style={{ color: 'var(--warn)', flex: 'none' }} />
                      <span><strong>Amarillo:</strong> sin ajustar — complétalo para que los precios sean más precisos.</span>
                    </div>
                  </div>
                </PopoverSurface>
              </Popover>
            </span>
            {canMutate && (
              <Button
                appearance="secondary"
                icon={<Delete24Regular />}
                onClick={() => void onDeleteProject()}
              >
                Eliminar proyecto
              </Button>
            )}
            {/* ci: sin botón de "armar": se trabaja todo en la vista de planos (las fichas de abajo
                llevan directo; la hoja vive allí). Decisión 2026-10-01. */}
            {esCi ? null : !hasQuote && isProcessing ? (
              <Tooltip
                content="Espera a que terminen de procesarse los documentos"
                relationship="label"
              >
                <Button appearance="primary" size="large" icon={<Sparkle24Regular />} disabled>
                  {labelArmar}
                </Button>
              </Tooltip>
            ) : (
              <Button
                appearance="primary"
                size="large"
                icon={<Sparkle24Regular />}
                disabled={cotizando}
                onClick={() => void onCotizar()}
                data-demo="armar-presupuesto"
              >
                {hasQuote ? 'Abrir presupuesto' : cotizando ? 'Armando…' : labelArmar}
              </Button>
            )}
          </div>
        </div>

        {/* Aviso VISIBLE (no solo el tooltip por hover del botón, que en iPad no
            se ve): mientras se procesan los documentos, "Armar presupuesto" está
            deshabilitado. El contexto de proyecto sí se puede ir llenando. */}
        {!hasQuote && isProcessing && (
          <MessageBar intent="info" style={{ marginBottom: 'var(--space-l)' }}>
            <MessageBarBody>
              {esCi ? (
                <>
                  Estamos leyendo tus planos y documentos. En cuanto terminen, toca un plano para{' '}
                  <strong>empezar a armar el presupuesto</strong>. Mientras tanto puedes ir llenando el{' '}
                  <strong>contexto de la obra</strong>.
                </>
              ) : (
                <>
                  Estamos leyendo tus documentos. En cuanto terminen vas a poder <strong>armar el
                  presupuesto</strong>. Y si vas a completar el <strong>contexto del proyecto</strong>,
                  conviene generar antes el <strong>resumen de la obra</strong>: te da los datos para
                  llenarlo.
                </>
              )}
            </MessageBarBody>
          </MessageBar>
        )}

        {/* ===== Tira de stats ===== */}
        <div className={styles.summary}>
          <div className={styles.cell}>
            <div className={styles.lab}>Conceptos</div>
            <div className={styles.big}>{fmtNum.format(total)}</div>
            <div className={styles.note}>{esCi ? 'en la hoja de presupuesto' : 'en el catálogo del concurso'}</div>
          </div>
          <div className={styles.cell}>
            <div className={styles.lab}>Presupuestado</div>
            <div className={styles.big}>{pct}%</div>
            <div className={styles.note}>
              {total > 0 ? `${fmtNum.format(priced)} de ${fmtNum.format(total)} con precio` : 'sin precios'}
            </div>
          </div>
          <div className={styles.cell}>
            <div className={styles.lab}>Monto estimado</div>
            {/* En LA MONEDA DE LA OBRA (ver lib/money.ts): un presupuesto chileno no
                se muestra en pesos mexicanos. Sin moneda conocida, sin símbolo. */}
            <div className={styles.big}>{amount > 0 ? formatoDeMoneda(project?.moneda).moneyCorto(amount) : '—'}</div>
            <div className={styles.note}>suma de importes</div>
          </div>
          {/* Tarjeta "Conceptos sin match" eliminada (reunión 2026-07-02):
              confundía en la vista de proyecto; los sin candidato se ven en
              la cotización y en análisis con IA. */}
        </div>

        {/* ===== Tabs ===== */}
        <div className={styles.tabs} role="tablist">
          <button
            role="tab"
            aria-selected={tab === 'docs'}
            className={mergeClasses(styles.tab, tab === 'docs' && styles.tabActive)}
            onClick={() => setTab('docs')}
          >
            {esCi ? 'Planos y documentos' : 'Documentos'} <span style={{ opacity: 0.6 }}>{files.length}</span>
          </button>
          {!esCi && (
            <button
              role="tab"
              aria-selected={tab === 'resumen'}
              className={mergeClasses(styles.tab, tab === 'resumen' && styles.tabActive)}
              onClick={() => setTab('resumen')}
              data-demo="tab-resumen"
            >
              Resumen del concurso
            </button>
          )}
          {juntaVisible && !esCi && (
            <button
              role="tab"
              aria-selected={tab === 'junta'}
              className={mergeClasses(styles.tab, tab === 'junta' && styles.tabActive, novJunta.visible && novStyles.halo)}
              onClick={() => { novJunta.marcarVisto(); setTab('junta'); }}
            >
              Junta de aclaraciones{' '}
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '9.5px',
                  letterSpacing: '0.06em',
                  padding: '2px 5px',
                  borderRadius: '5px',
                  background: 'var(--accent-weak)',
                  color: 'var(--accent)',
                  fontWeight: 700,
                  verticalAlign: 'middle',
                }}
              >
                RFI
              </span>
              {novJunta.visible && <span className={novStyles.badge}>novedad</span>}
            </button>
          )}
          <button
            role="tab"
            aria-selected={tab === 'chat'}
            className={mergeClasses(styles.tab, tab === 'chat' && styles.tabActive)}
            onClick={() => setTab('chat')}
            data-demo="tab-chat"
          >
            Preguntas a la inteligencia artificial
            {/* Recordatorio interno (solo ci y solo equipo de Ecosoft, nunca el cliente): decidir más adelante si el chat
                se queda o se oculta — en ci lo principal es el flujo de planos, no la consulta. */}
            {esCi && esStaffEcosoft && (
              <span
                style={{
                  marginLeft: 6, fontSize: '10px',
                  letterSpacing: '0.02em', padding: '2px 7px', borderRadius: '999px',
                  background: 'var(--warn-weak)', color: 'var(--warn)', fontWeight: 700,
                  verticalAlign: 'middle',
                }}
              >
                decidir si el chat es útil en ci
              </span>
            )}
          </button>
        </div>

        {/* ===== Contenido de la tab ===== */}
        {tab === 'resumen' && (
          <div className={styles.panel}>
            {/* El aviso de planos vive solo en la pestaña RFI: es donde (junto con la
                cotización) los planos se LEEN. El resumen solo los detecta, no los usa. */}
            <ObraSummary
              projectId={project.id}
              liveProgress={analyzeProgress}
              serverStatus={project.summaryStatus}
              canRegenerate={canMutate}
              onContextReset={refreshCtxAjustado}
              autoGenerate={autoGenSummary}
              processing={isProcessing}
              sinTexto={sinTexto}
            />
          </div>
        )}

        {tab === 'docs' && esCi && (
          <div className={styles.docsList}>
            {/* ci: la página del proyecto es la GALERÍA de planos. Los demás archivos quedan abajo
                como "Documentos de apoyo". Sin búsqueda en documentos ni "Volver a analizar": en
                ci los planos se miden en el visor, no se indexan. */}
            <div className={styles.panel} id="planos-de-la-obra">
              <div className={styles.sectionLabel}>Planos de la obra</div>
              <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-secondary)', marginBottom: 'var(--space-l)' }}>
                Toca un plano para abrirlo y medir. Todos suman en una sola hoja de presupuesto.
              </div>
              <PlanosGaleria
                planos={galeria.planos}
                error={galeria.error}
                onReintentar={() => void galeria.recargar()}
                canMutate={canMutate}
                subiendo={uploading}
                progreso={progress}
                onSubir={onUpload}
                onAbrir={(fileId) => navigate(`/project/${project.id}/planos/${fileId}`)}
                onElegirUnidad={async (fileId, unidad) => {
                  await setPlanoEscala(project.id, fileId, unidad);
                  await galeria.recargar();
                }}
                onEliminar={
                  canMutate
                    ? (fileId) => {
                        const f = files.find((x) => x.id === fileId);
                        if (f) void onDeleteFile(f);
                      }
                    : undefined
                }
              />
              {miniaturaIfc.fileId && project && (
                <MiniaturaIfcEnSegundoPlano
                  key={miniaturaIfc.fileId}
                  projectId={project.id}
                  fileId={miniaturaIfc.fileId}
                  onTermino={(ok) => miniaturaIfc.terminar(miniaturaIfc.fileId!, ok)}
                />
              )}
            </div>
            <div className={styles.panel}>
              <div className={styles.sectionLabel}>Documentos de apoyo</div>
              <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-secondary)', marginBottom: 'var(--space-l)' }}>
                Especificaciones, memorias o lo que quieras tener a la mano. No se miden.
              </div>
              {canMutate && (
                <div style={{ marginBottom: 'var(--space-l)' }}>
                  <DropZone
                    onFiles={onUpload}
                    busy={uploading}
                    progress={progress}
                    label={uploading ? `Subiendo ${Math.round(progress * 100)}%…` : undefined}
                  />
                </div>
              )}
              <FilesPanel
                projectId={project.id}
                files={documentosDeApoyo}
                onDelete={canMutate ? onDeleteFile : undefined}
                onRetry={canMutate ? onRetryFile : undefined}
                sinInsigniaNoCompatible
                textoVacio="No hay documentos de apoyo."
              />
            </div>
          </div>
        )}

        {tab === 'docs' && !esCi && (
          <div className={styles.docsList}>
            <IndexProgress
              projectId={project.id}
              status={project.indexStatus}
              chunksTotal={project.chunksTotal}
              chunksEmbedded={project.chunksEmbedded}
              parsingInFlight={parsingInFlight}
              onReindexed={() => void refresh()}
              canReindex={canMutate}
            />
            {canMutate && (
              <DropZone
                onFiles={onUpload}
                busy={uploading}
                progress={progress}
                label={uploading ? `Subiendo ${Math.round(progress * 100)}%…` : undefined}
              />
            )}
            <div className={styles.panel}>
              <div className={styles.sectionLabel}>{esCi ? 'Planos y documentos de la obra' : 'Documentos del concurso'}</div>
              {/* ci no cotiza desde un listado de conceptos: la HP sale de los planos. */}
              {!esCi && files.length > 0 && (canMutate || isDemo) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 'var(--space-l)' }}>
                  <label style={{ fontSize: 'var(--fs-200)', color: 'var(--text-secondary)', fontWeight: 600 }}>
                    Archivo con el listado de conceptos (para cotizar)
                  </label>
                  <Select
                    value={proposedCatalog?.id ?? ''}
                    disabled={!canMutate}
                    onChange={async (_, d) => {
                      const fid = d.value;
                      if (!fid || !canMutate) return;
                      await updateFileClassification(project.id, fid, 'catalogo');
                      // Una sola fuente: el elegido = catálogo, los demás que estaban como catálogo → otro.
                      setFiles((prev) => prev.map((x) =>
                        x.id === fid ? { ...x, classification: 'catalogo' }
                        : x.classification === 'catalogo' ? { ...x, classification: 'otro' }
                        : x,
                      ));
                    }}
                    style={{ maxWidth: 460 }}
                  >
                    <option value="">Selecciona el archivo del catálogo…</option>
                    {files.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.originalName}
                        {f.conceptsDetected ? ` — ${f.conceptsDetected} conceptos` : ''}
                      </option>
                    ))}
                  </Select>
                  <div style={{ fontSize: 'var(--fs-100)', color: 'var(--text-tertiary)' }}>
                    {isDemo
                      ? 'En el ejemplo ya está elegido el listado de conceptos usado para armar el presupuesto.'
                      : propuesta?.motivo === 'mas-conceptos'
                        ? `Lo elegimos nosotros: es donde encontramos más conceptos (${proposedCatalog!.conceptsDetected}). Puedes cambiarlo.`
                        : propuesta?.motivo === 'corazonada'
                          // Se dice que es una corazonada porque lo es: no se detectaron
                          // conceptos en ninguno. Callarlo era el bug — la pantalla pedía
                          // elegir y el botón elegía solo.
                          ? `No encontramos una lista de conceptos en ninguno de tus archivos. Vamos a usar «${proposedCatalog!.originalName}», que es el último que subiste. Si no es ese, elígelo aquí.`
                          : 'De este archivo se extraen los conceptos al cotizar. Al cotizar puedes cambiarlo.'}
                  </div>
                </div>
              )}
              <FilesPanel
                projectId={project.id}
                files={files}
                onDelete={canMutate ? onDeleteFile : undefined}
                onRetry={canMutate ? onRetryFile : undefined}
              />
              <div className={styles.trustbox} style={{ marginTop: 'var(--space-l)' }}>
                <ShieldCheckmark20Regular style={{ flexShrink: 0, color: 'var(--oficial)' }} />
                <div>
                  <b>Privados de tu organización.</b> Nadie fuera de Ecosoft puede verlos ni
                  usarlos, y puedes recuperarlos o eliminarlos cuando quieras. ECOSTOS es la base
                  oficial de Ecosoft.
                </div>
              </div>
            </div>
          </div>
        )}

        {juntaVisible && tab === 'junta' && (
          <div className={styles.panel}>
            <JuntaPanel
              projectId={project.id}
              project={project}
              isProcessing={isProcessing}
              canMutate={canMutate}
              planosPendientes={planosPendientes}
              planosCostoUsd={planosCostoUsd}
            />
          </div>
        )}

        {tab === 'chat' && (
          <div className={mergeClasses(styles.panel, styles.chatPanel)}>
            {isDemo ? (
              <DemoSampleChat />
            ) : (
              <ChatPanel
                projectId={project.id}
                files={files.map((f) => ({ id: f.id, originalName: f.originalName, classification: f.classification }))}
                indexStatus={project.indexStatus}
                chunksTotal={project.chunksTotal}
                chunksEmbedded={project.chunksEmbedded}
                parsingInFlight={parsingInFlight}
              />
            )}
          </div>
        )}
      </main>

    </div>
  );
}
